import React from 'react';
import { motion } from 'framer-motion';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { CreditCard, RefreshCw, Loader2 } from 'lucide-react';

export const BillingTab = ({
  billing, campus, pricePerUser,
  showPayment, setShowPayment,
  paymentForm, setPaymentForm,
  onInitiatePayment, processingPayment,
}) => {
  const isPaid = billing?.payment_status === 'paid';
  const isYearly = paymentForm.billing_period === 'yearly';
  const total = isYearly
    ? (pricePerUser * paymentForm.user_count * 12 * 0.8).toFixed(2) + '/year'
    : (pricePerUser * paymentForm.user_count).toFixed(2) + '/month';

  return (
    <div className="space-y-6" data-testid="campus-billing">
      <Card className="p-6  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]">
        <h3 className="text-lg font-semibold text-[var(--isp-heading)] mb-4 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-green-400" />
          Campus Billing
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="p-4 rounded-xl bg-slate-800/30 border border-[var(--isp-glass-border)]">
            <p className="text-xs text-[var(--isp-muted)] mb-1">Current Plan</p>
            <p className="text-lg font-bold text-white">{billing?.plan_name || campus.plan_name}</p>
          </div>
          <div className="p-4 rounded-xl bg-slate-800/30 border border-[var(--isp-glass-border)]">
            <p className="text-xs text-[var(--isp-muted)] mb-1">Status</p>
            <span className={`text-sm font-medium px-2 py-1 rounded-full ${isPaid ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}`}>
              {isPaid ? 'Active' : 'Trial'}
            </span>
          </div>
          <div className="p-4 rounded-xl bg-slate-800/30 border border-[var(--isp-glass-border)]">
            <p className="text-xs text-[var(--isp-muted)] mb-1">Monthly Estimate</p>
            <p className="text-lg font-bold text-white">
              ${billing?.monthly_total || (pricePerUser * (billing?.current_users || 1)).toFixed(2)}
            </p>
            <p className="text-[10px] text-[var(--isp-muted)]">
              ${pricePerUser}/user x {billing?.current_users || 1} users
            </p>
          </div>
        </div>

        {!showPayment && (
          <Button
            onClick={() => setShowPayment(true)}
            className="rounded-xl gap-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
            data-testid="renew-subscription-btn"
          >
            <RefreshCw className="w-4 h-4" />
            {isPaid ? 'Renew Subscription' : 'Activate Subscription'}
          </Button>
        )}

        {showPayment && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] space-y-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-[var(--isp-body)] block mb-1">Number of users</label>
                <input
                  type="number"
                  value={paymentForm.user_count}
                  onChange={e => setPaymentForm({ ...paymentForm, user_count: Number(e.target.value) })}
                  min={1}
                  max={campus.max_users}
                  className="w-full p-2 rounded-lg bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-white text-sm"
                  data-testid="payment-user-count"
                />
              </div>
              <div>
                <label className="text-xs text-[var(--isp-body)] block mb-1">Billing period</label>
                <select
                  value={paymentForm.billing_period}
                  onChange={e => setPaymentForm({ ...paymentForm, billing_period: e.target.value })}
                  className="w-full p-2 rounded-lg bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-white text-sm"
                  data-testid="payment-billing-period"
                >
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly (20% off)</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-[var(--isp-body)] block mb-1">Billing email</label>
                <input
                  type="email"
                  value={paymentForm.email}
                  onChange={e => setPaymentForm({ ...paymentForm, email: e.target.value })}
                  placeholder="billing@university.edu"
                  className="w-full p-2 rounded-lg bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-white text-sm"
                  data-testid="payment-email"
                />
              </div>
            </div>
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <p className="text-sm text-green-300 font-medium">Total: ${total}</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={onInitiatePayment}
                disabled={processingPayment}
                className="rounded-xl gap-2 bg-gradient-to-r from-green-500 to-emerald-500"
                data-testid="confirm-payment-btn"
              >
                {processingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                Proceed to Payment
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowPayment(false)}
                className="rounded-xl border-[var(--isp-glass-border)] text-[var(--isp-body)]"
              >
                Cancel
              </Button>
            </div>
          </motion.div>
        )}
      </Card>

      {billing?.transactions?.length > 0 && (
        <Card className="p-5  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]">
          <h4 className="text-sm font-semibold text-[var(--isp-body)] mb-3">Payment History</h4>
          <div className="space-y-2">
            {billing.transactions.map((tx) => {
              const statusCls = tx.status === 'completed'
                ? 'bg-green-500/20 text-green-400'
                : 'bg-amber-500/20 text-amber-400';
              return (
                <div
                  key={tx.reference || tx.created_at}
                  className="flex items-center justify-between p-3 rounded-xl bg-slate-800/30 border border-[var(--isp-glass-border)] text-sm"
                >
                  <div>
                    <p className="text-[var(--isp-heading)]">{tx.reference?.slice(0, 20)}...</p>
                    <p className="text-xs text-[var(--isp-muted)]">{tx.created_at?.slice(0, 10)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[var(--isp-heading)] font-medium">${tx.amount_usd?.toFixed(2)}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${statusCls}`}>{tx.status}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
};
